import React, { useState } from 'react';
import { Languages, Loader2 } from 'lucide-react';
import { useLanguage, Language } from '../../contexts/LanguageContext';
import { useTranslation } from 'react-i18next';
import { settingsService } from '../../services/SettingsService';
import { useAuth } from '../../contexts/AuthContext';

interface LanguageSelectorProps {
  className?: string;
  requireAuth?: boolean; // If true, will sync with backend
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ className = '', requireAuth = true }) => {
  const { currentLanguage, changeLanguage } = useLanguage();
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const languages: { code: Language; label: string; flag: string }[] = [
    { code: 'en', label: t('language.english'), flag: '🇺🇸' },
    { code: 'es', label: t('language.spanish'), flag: '🇪🇸' },
    { code: 'es-MX', label: t('language.spanishMexican'), flag: '🇲🇽' },
    { code: 'es-CL', label: t('language.spanishChilean'), flag: '🇨🇱' },
    { code: 'es-EC', label: t('language.spanishEcuadorian'), flag: '🇪🇨' },
    { code: 'pt', label: t('language.portuguese'), flag: '🇵🇹' },
  ];

  const setBackendLanguage = async (lang: Language) => {
    try {
      // Save to user's personal language preference — does NOT affect org/catalog language
      await settingsService.setUserLanguage(lang);
    } catch (error) {
      console.error('Failed to set language on backend:', error);
      throw error;
    }
  }

  const handleLanguageChange = async (newLang: Language) => {
    setIsLoading(true);
    try {
      await changeLanguage(newLang);

      // Only sync with backend if user is authenticated and requireAuth is true
      if (requireAuth && currentUser) {
        await setBackendLanguage(newLang);
      }
    } catch (error) {
      console.error('Failed to change language:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 rounded-lg px-3 py-2 text-gray-700 transition-colors">
        {isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Languages className="w-4 h-4" />
        )}
        <select
          value={currentLanguage}
          onChange={(e) => handleLanguageChange(e.target.value as Language)}
          disabled={isLoading}
          className="bg-transparent text-gray-700 border-none outline-none cursor-pointer text-sm font-medium appearance-none pr-6 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23374151' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
            backgroundPosition: 'right 0.5rem center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '1rem'
          }}
        >
          {languages.map((lang) => (
            <option
              key={lang.code}
              value={lang.code}
              className="bg-white text-gray-900"
            >
              {lang.flag} {lang.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default LanguageSelector;
